import fetch from 'cross-fetch';

import { HttpResponse } from 'buzz_lib_models';

import { Nullable } from 'buzz_lib_building_blocks';

import { ensure } from '../types';

import { getAccessToken } from '../authentication/getAccessToken';
import { Logger } from '../logging/Logger';
import { buildUrl } from '../url/urlBuilderService';
import { HttpGetOptions } from './HttpGetOptions';

enum HttpMethod {
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE',
}

interface RequestQuery {
  [key: string]: any;
}
interface RequestBody {
  [key: string]: any;
}
interface RequestHeaders {
  [key: string]: any;
}

export interface HttpService {
  get(
    url: string,
    query: RequestQuery,
    signal?: AbortSignal,
    headers?: RequestHeaders,
    options?: HttpGetOptions,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>>;
  post(
    url: string,
    body?: { [key: string]: any },
    headers?: RequestHeaders,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>>;
  postFile(
    url: string,
    body: FormData,
    headers?: RequestHeaders,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>>;
  put(
    url: string,
    body?: RequestBody,
    headers?: RequestHeaders,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>>;
  delete(
    url: string,
    body?: RequestBody,
    headers?: RequestHeaders,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>>;
}

interface HttpServiceParams {
  shouldLogAllRequests: boolean;
  logger?: Logger;
}

export const httpService = (params?: HttpServiceParams): HttpService => {
  const baseHeaders: RequestHeaders = {
    'Content-Type': 'application/json',
  };

  if (params?.shouldLogAllRequests) ensure(params?.logger, 'Logging requests is enabled, but Logger is not provided');

  const httpGet = (
    url: string,
    query: RequestQuery = {},
    signal?: AbortSignal,
    headers: RequestHeaders = baseHeaders,
    options?: HttpGetOptions,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>> => {
    const fetchParams = _createFetchParams(HttpMethod.Get, headers);

    if (signal) {
      fetchParams.signal = signal;
    }
    const requestUrl = buildUrl(url, query, options?.stringifyArrayParam);

    return _httpFetch(requestUrl, fetchParams).then(response => {
      _handleResponseLogging(response, isLoggingEnabled);

      return response;
    });
  };

  const httpPost = (
    url: string,
    body: RequestBody = {},
    headers: RequestHeaders = baseHeaders,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>> => {
    const fetchParams = _createFetchParams(HttpMethod.Post, headers, body);
    _handleRequestLogging(url, headers, fetchParams.body, isLoggingEnabled);

    return _httpFetch(url, fetchParams).then(response => {
      _handleResponseLogging(response, isLoggingEnabled);

      return response;
    });
  };

  const httpPostFile = (
    url: string,
    body: FormData,
    headers: RequestHeaders = {},
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>> => {
    const fetchParams = _createFileFetchParams(HttpMethod.Post, headers, body);
    _handleRequestLogging(url, headers, fetchParams.body, isLoggingEnabled);

    return _httpFetch(url, fetchParams);
  };

  const httpPut = (
    url: string,
    body: RequestBody = {},
    headers: RequestHeaders = baseHeaders,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>> => {
    const fetchParams = _createFetchParams(HttpMethod.Put, headers, body);
    _handleRequestLogging(url, headers, fetchParams.body, isLoggingEnabled);

    return _httpFetch(url, fetchParams);
  };

  const httpDelete = (
    url: string,
    body: RequestBody = {},
    headers: RequestHeaders = baseHeaders,
    isLoggingEnabled?: boolean
  ): Promise<Nullable<HttpResponse>> => {
    const fetchParams = _createFetchParams(HttpMethod.Delete, headers, body);
    _handleRequestLogging(url, headers, fetchParams.body, isLoggingEnabled);

    return _httpFetch(url, fetchParams);
  };

  const _parseResponse = async (response: Response): Promise<HttpResponse> => {
    const httpResponse: HttpResponse = {
      StatusCode: response.status,
    };

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      httpResponse.Body = await response.json();
    } else if (contentType && (contentType.includes('application/octet-stream') || contentType.includes('image'))) {
      httpResponse.Body = response;
    } else {
      httpResponse.BodyText = await response.text();
    }

    return httpResponse;
  };

  const _httpFetch = async (url: string, params?: RequestInit) => {
    try {
      const accessToken = await getAccessToken();

      const response: Response = await fetch(url, {
        ...params,
        headers: {
          ...params?.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return await _parseResponse(response);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  };

  const _logMessage = (message: string) => params?.logger?.log(message);

  const _createRequestLogMessage = (
    url: string,
    headers: RequestHeaders,
    body?: Nullable<BodyInit>
  ) => `Outbound HTTP Request: 
        Target URL: ${url},
        Body: ${body ? JSON.stringify(body) : 'null'},
        Headers: ${JSON.stringify(headers)}
        `;

  const _createResponseLogMessage = (response: Nullable<HttpResponse>) => {
    const stringifiedResponse = response
      ? `Status Code: ${response?.StatusCode},
         Body: ${JSON.stringify(response?.Body)},`
      : 'null';

    return `HTTP Response: ${stringifiedResponse}`;
  };

  const _handleRequestLogging = (
    url: string,
    headers: RequestHeaders,
    body?: Nullable<BodyInit>,
    isLoggingEnabled?: boolean
  ) => {
    if (isLoggingEnabled || params?.shouldLogAllRequests) {
      const requestLogMessage = _createRequestLogMessage(url, headers, body);
      _logMessage(requestLogMessage);
    }
  };

  const _handleResponseLogging = (response: Nullable<HttpResponse>, isLoggingEnabled?: boolean) => {
    if (isLoggingEnabled || params?.shouldLogAllRequests) {
      const responseLogMessage = _createResponseLogMessage(response);
      _logMessage(responseLogMessage);
    }
  };

  const _createFileFetchParams = (method: HttpMethod, headers: RequestHeaders, body: FormData): RequestInit => ({
    method,
    body,
    headers,
  });

  const _createFetchParams = (method: HttpMethod, headers: RequestHeaders, body?: RequestBody): RequestInit => ({
    method,
    body: body ? JSON.stringify(body) : undefined,
    headers,
  });

  return {
    get: httpGet,
    post: httpPost,
    postFile: httpPostFile,
    put: httpPut,
    delete: httpDelete,
  };
};
